import React, { useState, useCallback } from 'react';
import { Modal, Checkbox } from '../..';
import {
	Divider,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	ListItem,
	ListItemText,
	Grid,
	FormControlLabel
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { withStyles } from '@material-ui/core/styles';
import { styles } from '../MarketingCampaigns.styles';
import { mainColor } from '../../../constants';
import moment from 'moment';
import { CampaignType, CAMPAIGN_ITEMS } from '../../../constants/marketingCampaign';
import Collapse from '@material-ui/core/Collapse';
import { InputField, DatePickerField } from './FormFields'
import { SUITABLE_ORDER_TYPES } from '../../../constants/dataConfig';
import { CampaignPromoModalProps } from '../MarketingCampaigns.types';

const StyledAccordion = withStyles({
	root: {
		border: 'none',
		boxShadow: 'none',
		margin: '0 !important',
		padding: '0',
		width: '100%',
		overflow: 'visible!important',
		position: 'relative',
		'&:before': { display: 'none' },
		'&$expanded': {
			margin: '0 !important'
		},
	},
	expanded: {},
})(Accordion);

const StyledAccordionSummary = withStyles({
	root: {
		minHeight: '45px !important',
		height: '45px',
		padding: '0 12px',
		'&$expanded': { minHeight: '45px !important', height: '45px' },
	},
	content: {
		margin: '0 !important',
		'&$expanded': { margin: '0 !important' },
		alignItems: 'center',
	},
	expandIcon: {
		marginRight: '0px !important',
		transform: 'rotate(0deg)',
		transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
		'&$expanded': {
			transform: 'rotate(180deg)',
		},
		'&:hover': {
			backgroundColor: `rgba(${mainColor.rgb}, 0.5)`
		},
		padding: '7px 7px 7px 7px'
	},
	expanded: {},
})(AccordionSummary);

const CampaignPromoModal = ({
	classes,
	showModal,
	loading,
	handleClose,
	selectedCampaign,
	onClick,
	isUpdate,
	availableOrderTypes = []
}: CampaignPromoModalProps) => {
	const { type, config, dateEnd, dateStart, shopIds, marketingCampaignKey } = selectedCampaign;
	const { title, description, inputDescription, inputText, id, valueType } = isUpdate ? CAMPAIGN_ITEMS.filter(item => type === item.id)[0] : selectedCampaign;
	const defaultStartTime = moment().add(30, 'minutes');

	const campaignOrderTypes = config?.orderTypes ?? selectedCampaign?.orderTypes ?? [];
	const initialOrderTypes = isUpdate && campaignOrderTypes.length > 0
		? campaignOrderTypes
		: availableOrderTypes;

	const isFreeDelivery = id === CampaignType.FREE_DELIVERY_AFTER_THRESHOLD;

	const [formValues, setFormValues] = useState({
		startDate: dateStart ? moment(dateStart).toDate() : moment().toDate(),
		endDate: dateEnd ? moment(dateEnd).toDate() : moment().add(1, 'day').toDate(),
		startTime: dateStart ? moment(dateStart).format('HH:mm') : defaultStartTime.format('HH:mm'),
		endTime: dateEnd ? moment(dateEnd).format('HH:mm') : '23:59',
		discount: config?.discount?.value || null,
		threshold: config?.threshold || null,
		type: id,
		shopIds: shopIds || [],
		valueType,
		marketingCampaignKey,
		orderTypes: isFreeDelivery ? ['delivery'] : initialOrderTypes
	});

	const [expandedSection, setExpandedSection] = useState(inputText ? 'discount' : 'threshold');

	const handleDateChange = useCallback((date, field) => {
		setFormValues(prev => ({
			...prev,
			[field]: date
		}));
	}, []);

	const handleInputChange = useCallback((e, field) => {
		const value = e?.target?.value;
		if (value === undefined) return;

		setFormValues(prev => ({ ...prev, [field]: value }));
	}, []);

	const toggleSection = (section) => {
		setExpandedSection(prev => (prev === section ? null : section));
	};

	const handleOrderTypeToggle = useCallback((orderType) => {
		setFormValues(prev => {
			const currentOrderTypes = prev.orderTypes || [];
			const isSelected = currentOrderTypes.includes(orderType);

			if (isSelected) {
				if (currentOrderTypes.length === 1) return prev;
				return {
					...prev,
					orderTypes: currentOrderTypes.filter(type => type !== orderType)
				};
			} else {
				return {
					...prev,
					orderTypes: [...currentOrderTypes, orderType]
				};
			}
		});
	}, []);

	const isDiscountError =
		formValues.discount !== null &&
		(
			(id === CampaignType.GLOBAL_PRODUCT_DISCOUNT_AFTER_THRESHOLD && formValues.discount > 100) ||
			formValues.discount <= 0
		);
	return (
		<Modal
			color="secondary"
			title={title}
			header
			size="xl"
			loading={loading}
			btnTxt={`${isUpdate ? 'Modifier ' : 'Lancer '} la campagne `}
			modalBodyClass={`py-0 ${classes.modalHight}`}
			onClick={() => onClick(formValues)}
			isOpen={showModal}
			handleClose={handleClose}
			showCancelBtn

		>

			<p className='pt-2'>{description}</p>
			<div>
				{inputText && (
					<>
						<StyledAccordion
							expanded={expandedSection === 'discount'}
							onChange={() => toggleSection('discount')}
						>
							<StyledAccordionSummary
								className='p-0'
								expandIcon={<ExpandMoreIcon style={{ color: mainColor.colorBlack }} />}
							>
								<ListItem className='p-0'>
									<ListItemText
										style={{ color: mainColor.colorBlack }}
										primary={<span className={classes.typo}>{inputText}</span>}
									/>
								</ListItem>
							</StyledAccordionSummary>
							<AccordionDetails className='p-0'>
								<InputField
									description={inputDescription}
									value={formValues.discount}
									onChange={(e) => handleInputChange(e, 'discount')}
									field="discount"
									valueSymbol={id === CampaignType.GLOBAL_PRODUCT_DISCOUNT_AFTER_THRESHOLD ? '%' : '€'}
									error={isDiscountError}
								/>
							</AccordionDetails>
						</StyledAccordion>
						<Divider className={classes.dividerStyle} />
					</>
				)}

				<StyledAccordion
					expanded={expandedSection === 'threshold'}
					onChange={() => toggleSection('threshold')}
				>
					<StyledAccordionSummary
						className='p-0'
						expandIcon={<ExpandMoreIcon style={{ color: mainColor.colorBlack }} />}
					>
						<ListItem className='p-0'>
							<ListItemText
								style={{ color: mainColor.colorBlack }}
								primary={<span className={classes.typo}>Panier minimum</span>}
							/>
						</ListItem>
					</StyledAccordionSummary>
					<AccordionDetails className='p-0'>
						<InputField
							description="Pour activer l'offre le client doit dépenser"
							value={formValues.threshold}
							onChange={(e) => handleInputChange(e, 'threshold')}
							field="threshold"
							valueSymbol='€'
							error={formValues.threshold !== null && formValues.threshold <= 0}
						/>
					</AccordionDetails>
				</StyledAccordion>
				<Divider className={classes.dividerStyle} />

				<StyledAccordion
					expanded={expandedSection === 'dates'}
					onChange={() => toggleSection('dates')}
				>
					<StyledAccordionSummary className='p-0' expandIcon={<ExpandMoreIcon style={{ color: mainColor.colorBlack }} />}>
						<ListItem className='p-0'>
							<ListItemText style={{ color: mainColor.colorBlack }} primary={<span className={classes.typo}>Dates de l'offre</span>} />
						</ListItem>
					</StyledAccordionSummary>
				</StyledAccordion>
				<Collapse in={expandedSection === 'dates'} timeout={300} unmountOnExit>
					<div className={`${classes.section} px-1 pr-3`}>
						<DatePickerField
							label="Date de début le"
							date={formValues.startDate}
							time={formValues.startTime}
							onDateChange={(date) => handleDateChange(date, 'startDate')}
							onTimeChange={(e) => handleInputChange(e, 'startTime')}
							minDate={moment().toDate()}

						/>

						<DatePickerField
							label="Date de fin le"
							date={formValues.endDate}
							time={formValues.endTime}
							onDateChange={(date) => handleDateChange(date, 'endDate')}
							onTimeChange={(e) => handleInputChange(e, 'endTime')}
							minDate={formValues.startDate}

						/>
					</div>
				</Collapse>

				<Divider className={classes.dividerStyle} />

				{!isFreeDelivery && (
					<>
						<StyledAccordion
							expanded={expandedSection === 'orderTypes'}
							onChange={() => toggleSection('orderTypes')}
						>
							<StyledAccordionSummary className='p-0' expandIcon={<ExpandMoreIcon style={{ color: mainColor.colorBlack }} />}>
								<ListItem className='p-0'>
									<ListItemText style={{ color: mainColor.colorBlack }} primary={<span className={classes.typo}>Choix des modes de vente</span>} />
								</ListItem>
							</StyledAccordionSummary>
						</StyledAccordion>
						<Collapse in={expandedSection === 'orderTypes'} timeout={300} unmountOnExit>
							<div className='px-1 py-2'>
								<Grid container spacing={1}>
									{availableOrderTypes.map((orderType) => (
										<Grid item xs={12} sm={6} md={4} key={orderType}>
											<FormControlLabel
												control={
													<Checkbox
														value={formValues.orderTypes?.includes(orderType)}
														onClick={() => handleOrderTypeToggle(orderType)}
														color="primary"
													/>
												}
												label={SUITABLE_ORDER_TYPES[orderType] || orderType}
												style={{ marginLeft: 0, marginRight: 0, whiteSpace: 'nowrap' }}
											/>
										</Grid>
									))}
								</Grid>
							</div>
						</Collapse>
					</>
				)}

			</div>
		</Modal>
	);
};

export default withStyles(styles)(CampaignPromoModal);
